import React, {useEffect, useState} from 'react';
import '../Body.css';
import ApiService from "../services/ApiService";

// Note: We keep the same calling style as other components (props object),
// so ApiService can continue to read token.token.
function EigenerUser(token) {
    const [loading, setLoading] = useState(false);
    const [mannschaften, setMannschaften] = useState([]);
    const [filteredMannschaften, setFilteredMannschaften] = useState([]);
    const [selectedMannschaftIds, setSelectedMannschaftIds] = useState(new Set());
    
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    
    const [filterDisziplin, setFilterDisziplin] = useState('');
    const [filterText, setFilterText] = useState('');
    
    const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    
    // Get unique disziplinen from mannschaften
    const disziplinen = [...new Map(mannschaften.map(m => [m.disziplin.id, m.disziplin])).values()];

    useEffect(() => {
        setLoading(true);
        ApiService.fetchListen("mannschaft")
            .then(data => {
                const sorted = [...data].sort((a, b) => {
                    const disziplinCmp = a.disziplin.name.localeCompare(b.disziplin.name);
                    if (disziplinCmp !== 0) return disziplinCmp;
                    return a.name.localeCompare(b.name);
                });
                setMannschaften(sorted);
                setFilteredMannschaften(sorted);
                setLoading(false);
            })
            .catch(error => {
                console.error('Fehler beim Laden der Mannschaften:', error);
                setLoading(false);
            });
    }, []);

    // Filter mannschaften by disziplin and text
    useEffect(() => {
        let filtered = mannschaften;
        
        if (filterDisziplin) {
            filtered = filtered.filter(m => m.disziplin.id === parseInt(filterDisziplin));
        }
        if (filterText) {
            const searchText = filterText.toLowerCase();
            filtered = filtered.filter(m =>
                m.name.toLowerCase().includes(searchText) ||
                m.verein.name.toLowerCase().includes(searchText)
            );
        }
        setFilteredMannschaften(filtered);
    }, [filterDisziplin, filterText, mannschaften]);

    const handleMannschaftToggle = (mannschaftId) => {
        const newSelected = new Set(selectedMannschaftIds);
        if (newSelected.has(mannschaftId)) {
            newSelected.delete(mannschaftId);
        } else {
            newSelected.add(mannschaftId);
        }
        setSelectedMannschaftIds(newSelected);
    };

    const handleSelectAll = () => {
        if (selectedMannschaftIds.size === filteredMannschaften.length) {
            // Deselect all
            const newSelected = new Set();
            filteredMannschaften.forEach(m => {
                if (selectedMannschaftIds.has(m.id)) {
                    newSelected.delete(m.id);
                }
            });
            setSelectedMannschaftIds(newSelected);
        } else {
            // Select all filtered
            const newSelected = new Set(selectedMannschaftIds);
            filteredMannschaften.forEach(m => newSelected.add(m.id));
            setSelectedMannschaftIds(newSelected);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaveSuccessful(false);
        setHasError(false);
        setErrorMessage('');

        // Validation
        if (!username.trim()) {
            setHasError(true);
            setErrorMessage('Bitte geben Sie einen Benutzernamen ein');
            return;
        }

        if (!password) {
            setHasError(true);
            setErrorMessage('Bitte geben Sie ein Passwort ein');
            return;
        }

        if (password !== passwordConfirm) {
            setHasError(true);
            setErrorMessage('Die Passwörter stimmen nicht überein');
            return;
        }

        const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;
        if (!passwordRegex.test(password)) {
            setHasError(true);
            setErrorMessage('Passwort muss mindestens 8 Zeichen lang sein und jeweils mindestens einen Großbuchstaben, Kleinbuchstaben und ein Sonderzeichen enthalten');
            return;
        }

        try {
            await ApiService.createUser(token, username, password, selectedMannschaftIds);
            setIsSaveSuccessful(true);
            setErrorMessage('User erfolgreich erstellt!');
            // Reset form
            setUsername('');
            setPassword('');
            setPasswordConfirm('');
            setSelectedMannschaftIds(new Set());
        } catch (error) {
            setHasError(true);
            setErrorMessage(error.message || 'Fehler beim Erstellen des Users');
        }
    };

    // Group mannschaften by disziplin
    const groupedMannschaften = filteredMannschaften.reduce((acc, mannschaft) => {
        const disziplinName = mannschaft.disziplin.name;
        if (!acc[disziplinName]) {
            acc[disziplinName] = [];
        }
        acc[disziplinName].push(mannschaft);
        return acc;
    }, {});

    if (loading) {
        return <p>Loading...</p>;
    }

    return (
        <div className="element-content element-background">
            <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
                <h2>Eigener User anlegen</h2>
                
                <form onSubmit={handleSubmit} style={{ marginBottom: '30px' }}>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Benutzername:
                        </label>
                        <input
                            type="text"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            style={{ width: '100%', maxWidth: '400px', padding: '8px' }}
                            required
                        />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Passwort:
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            style={{ width: '100%', maxWidth: '400px', padding: '8px' }}
                            required
                        />
                    </div>
                    
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                            Passwort bestätigen:
                        </label>
                        <input
                            type="password"
                            value={passwordConfirm}
                            onChange={(e) => setPasswordConfirm(e.target.value)}
                            style={{ width: '100%', maxWidth: '400px', padding: '8px' }}
                            required
                        />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <p style={{ fontSize: '0.95em', color: '#333' }}>
                            wähle alle Mannschaften (aller Disziplinen), für die dieser User verantwortlich ist.
                            Es kann mehrere User pro Verein geben. (Jedoch maximal 2 User pro Mannschaft)
                        </p>
                    </div>

                    {/* Filter */}
                    <div style={{ marginBottom: '20px', display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                        <div style={{ minWidth: '220px' }}>
                            <label style={{ display: 'block', marginBottom: '5px' }}>Filter:</label>
                            <select
                                value={filterDisziplin}
                                onChange={(e) => setFilterDisziplin(e.target.value)}
                                style={{ padding: '8px', width: '100%' }}
                            >
                                <option value="">Alle Disziplinen</option>
                                {disziplinen.map(disziplin => (
                                    <option key={disziplin.id} value={disziplin.id}>
                                        {disziplin.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={{ minWidth: '260px', flex: '1 1 260px' }}>
                            <input
                                type="text"
                                value={filterText}
                                onChange={(e) => setFilterText(e.target.value)}
                                placeholder="Mannschaft oder Verein..."
                                style={{ padding: '8px', width: '100%' }}
                            />
                        </div>
                    </div>

                    {/* Mannschaften Liste */}
                    <div style={{ 
                        border: '1px solid #ddd', 
                        borderRadius: '4px', 
                        padding: '15px', 
                        maxHeight: '500px', 
                        overflowY: 'auto',
                        marginBottom: '20px'
                    }}>
                        {Object.keys(groupedMannschaften).length === 0 ? (
                            <p>Keine Mannschaften gefunden</p>
                        ) : (
                            Object.entries(groupedMannschaften).map(([disziplinName, mannschaftenList]) => (
                                <div key={disziplinName} style={{ marginBottom: '25px' }}>
                                    <h4 style={{ 
                                        marginBottom: '10px', 
                                        paddingBottom: '5px', 
                                        borderBottom: '2px solid #ccc',
                                        color: '#333'
                                    }}>
                                        {disziplinName}
                                    </h4>
                                    <div style={{ display: 'grid', gap: '8px' }}>
                                        {mannschaftenList.map(mannschaft => (
                                            <label 
                                                key={mannschaft.id} 
                                                style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    padding: '5px',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={selectedMannschaftIds.has(mannschaft.id)}
                                                    onChange={() => handleMannschaftToggle(mannschaft.id)}
                                                    style={{ marginRight: '10px', cursor: 'pointer' }}
                                                />
                                                <span>
                                                    {mannschaft.name} ({mannschaft.verein.name})
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <p>
                            <strong>Ausgewählt: {selectedMannschaftIds.size} Mannschaft(en)</strong>
                        </p>
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                        <button
                            type="submit"
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '16px'
                            }}
                        >
                            User erstellen
                        </button>
                    </div>

                    {errorMessage && (
                        <div style={{
                            padding: '10px',
                            borderRadius: '4px',
                            backgroundColor: hasError ? '#ffebee' : '#e8f5e9',
                            color: hasError ? '#c62828' : '#2e7d32',
                            marginTop: '10px'
                        }}>
                            {errorMessage}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default EigenerUser;

